import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import APIServices from '../services/APIServices';
import ScheduleMessagePicker from './ScheduleMessagePicker';
import appToast, { TOAST_TYPE } from 'utils/AppToast';

export const fmtScheduledTimeLabel = (iso) => {
  const dt = new Date(iso);
  const now = new Date();
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dtMs   = new Date(dt.getFullYear(),  dt.getMonth(),  dt.getDate()).getTime();
  const diff   = dtMs - todayMs;
  const timeStr = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diff === 0)         return `today at ${timeStr}`;
  if (diff === 86400000)  return `tomorrow at ${timeStr}`;
  return `on ${dt.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })} at ${timeStr}`;
};

const getInitials = (name) =>
  (name || '?').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

// ── icons ─────────────────────────────────────────────────────────────────────
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2L12 4.5L4.5 12H2V9.5L9.5 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    <path d="M8 3.5L10.5 6" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);
const RescheduleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M7 4v3l2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);
const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M4 2.5l7 4.5-7 4.5V2.5z" fill="currentColor"/>
  </svg>
);
const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 3.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M5.5 3.5V2.4h3v1.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4 5l.45 6.1c.04.5.45.9.95.9h3.2c.5 0 .91-.4.95-.9L10 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 6.5v3M8 6.5v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

// ── component ──────────────────────────────────────────────────────────────────
const ScheduledMessagesList = ({ projectId, requestId, type, show, onClose, onMessagesChange }) => {
  const [messages,      setMessages]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [hoveredId,     setHoveredId]     = useState(null);
  const [editingId,     setEditingId]     = useState(null);
  const [editText,      setEditText]      = useState('');
  const [reschedulingId, setReschedulingId] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      let url = `/scheduled-messages?projectId=${projectId}`;
      if (requestId) url += `&requestId=${requestId}`;
      if (type)      url += `&type=${type}`;
      const res = await APIServices.get(url);
      setMessages(Array.isArray(res) ? res : []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId, requestId, type]);

  useEffect(() => {
    if (show) fetchMessages();
  }, [show, fetchMessages]);

  const handleSendNow = async (msg) => {
    try {
      await APIServices.post(`/scheduled-messages/${msg._id}/send-now`);
      appToast('Message sent!', TOAST_TYPE.SUCCESS);
      setMessages(prev => {
        const next = prev.filter(m => m._id !== msg._id);
        onMessagesChange?.(next);
        return next;
      });
    } catch {
      appToast('Failed to send message.', TOAST_TYPE.ERROR);
    }
  };

  const handleDelete = async (id) => {
    try {
      await APIServices.delete(`/scheduled-messages/${id}`);
      appToast('Message deleted.', TOAST_TYPE.SUCCESS);
      setMessages(prev => {
        const next = prev.filter(m => m._id !== id);
        onMessagesChange?.(next);
        return next;
      });
    } catch {
      appToast('Failed to delete message.', TOAST_TYPE.ERROR);
    }
  };

  const saveEdit = async (id) => {
    try {
      await APIServices.put(`/scheduled-messages/${id}`, { message: editText });
      setMessages(prev => prev.map(m => m._id === id ? { ...m, message: editText } : m));
      appToast('Message updated.', TOAST_TYPE.SUCCESS);
    } catch {
      appToast('Failed to update message.', TOAST_TYPE.ERROR);
    }
    setEditingId(null);
  };

  const handleReschedule = async (iso) => {
    const id = reschedulingId;
    setReschedulingId(null);
    try {
      await APIServices.put(`/scheduled-messages/${id}`, { scheduledAt: iso });
      setMessages(prev => {
        const next = prev.map(m => m._id === id ? { ...m, scheduledAt: iso } : m);
        onMessagesChange?.(next);
        return next;
      });
      appToast('Message rescheduled.', TOAST_TYPE.SUCCESS);
    } catch {
      appToast('Failed to reschedule message.', TOAST_TYPE.ERROR);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered className="sml-modal-root">
        <Modal.Header closeButton>
          <Modal.Title className="sml-modal-title">Scheduled messages</Modal.Title>
        </Modal.Header>

        <Modal.Body className="sml-body">
          {loading ? (
            <div className="sml-empty">Loading…</div>
          ) : messages.length === 0 ? (
            <div className="sml-empty">No scheduled messages for this project.</div>
          ) : (
            <div className="sml-list">
              {messages.map(msg => (
                <div
                  key={msg._id}
                  className={`sml-item${hoveredId === msg._id ? ' sml-item--hovered' : ''}`}
                  onMouseEnter={() => setHoveredId(msg._id)}
                  onMouseLeave={() => setHoveredId(null)}>

                  {/* Avatar */}
                  <div className="sml-avatar">
                    {msg.createdBy?.profilePic
                      ? <img src={msg.createdBy.profilePic} alt="" className="sml-avatar__img" />
                      : <span className="sml-avatar__initials">{getInitials(msg.createdBy?.fullName)}</span>}
                  </div>

                  {/* Content */}
                  <div className="sml-content">
                    <div className="sml-name">{msg.createdBy?.fullName || 'You'}</div>

                    {editingId === msg._id ? (
                      <div className="sml-edit-area">
                        <textarea
                          className="sml-edit-input"
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={2}
                          autoFocus
                        />
                        <div className="sml-edit-btns">
                          <button className="sml-ebtn sml-ebtn--save" onClick={() => saveEdit(msg._id)}>Save</button>
                          <button className="sml-ebtn sml-ebtn--cancel" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="sml-preview">
                        {msg.message
                          ? msg.message
                          : msg.attachments?.length > 0
                          ? `[${msg.attachments.length} attachment${msg.attachments.length > 1 ? 's' : ''}]`
                          : '—'}
                      </div>
                    )}

                    <div className="sml-time-label">
                      Send {fmtScheduledTimeLabel(msg.scheduledAt)}
                    </div>
                  </div>

                  {/* Hover action buttons */}
                  {(hoveredId === msg._id || reschedulingId === msg._id) && editingId !== msg._id && (
                    <div className="sml-actions">
                      <button className="sml-action-btn" title="Edit message"
                        onClick={() => { setEditingId(msg._id); setEditText(msg.message || ''); }}>
                        <PencilIcon />
                      </button>
                      <button className="sml-action-btn" title="Reschedule"
                        onClick={(e) => { e.stopPropagation(); setReschedulingId(prev => prev === msg._id ? null : msg._id); }}>
                        <RescheduleIcon />
                      </button>
                      <button className="sml-action-btn" title="Send now"
                        onClick={() => handleSendNow(msg)}>
                        <PlayIcon />
                      </button>
                      <button className="sml-action-btn sml-action-btn--danger" title="Delete message"
                        onClick={() => handleDelete(msg._id)}>
                        <DeleteIcon />
                      </button>
                    </div>
                  )}
                  {reschedulingId === msg._id && (
                    <div className="sml-reschedule-popover">
                      <ScheduleMessagePicker
                        openModalDirectly
                        initialScheduledAt={msg.scheduledAt}
                        onSchedule={handleReschedule}
                        onCancel={() => setReschedulingId(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ScheduledMessagesList;
